import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'classnames';
import i18n from 'i18next';
import _debounce from 'lodash/debounce';

import { DEVICE_TYPE, SEARCH_PATH } from '@/services/Constants';
import { historyPush } from '@/services/History';

import Button from '@/components/button/Button';
import { SearchInputProps } from '@/components/input/types';

const SearchInput = ({
	autoFocus,
	className,
	color,
	delayedSearchValue = 400,
	focusOnMount = false,
	goBack,
	goToSearch,
	hideBackBtn,
	needToShowBackBtn,
	onChange = () => {},
	placeholder = i18n.t('components.input.search_input_default_placeholder') as string,
	searchBar = true,
	value,
	withUpgradeBtn,
	countryToggle,
}: SearchInputProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const inputField = useRef<HTMLInputElement | null>(null);
	const [searchValue, setSearchValue] = useState<string>(value ?? '');
	const [showBackBtn, setShowBackBtn] = useState<boolean>(false);

	useEffect(() => {
		if (needToShowBackBtn) {
			setShowBackBtn(true);
			inputField.current?.focus();
		}

		if (focusOnMount) {
			inputField.current?.focus();
		}

		return () => {
			if (DEVICE_TYPE.type === 'ios') window.dispatchEvent(new Event('keyboardDidHide'));
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (typeof value === 'string') setSearchValue(value);
	}, [value]);

	useEffect(() => {
		if (autoFocus !== undefined && inputField.current) {
			setTimeout(() => {
				inputField.current?.focus();
			}, 100);
		}
	}, [autoFocus]);

	const delayedQuery = useCallback(
		_debounce((query: string) => onChange(query), delayedSearchValue),
		// re-create debounce when countryToggle changes to mimic original behavior
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[countryToggle, delayedSearchValue, onChange],
	);

	const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		delayedQuery(e.target.value);
		setSearchValue(e.target.value);
	};

	const goToPreviousRoute = () => {
		if (DEVICE_TYPE.type === 'ios') window.dispatchEvent(new Event('keyboardDidHide'));
		goBack ? goBack() : navigate(-1);
	};

	const onClick = () => {
		// reveal back button if nothing typed yet but needToShowBackBtn is requested
		if (!searchValue && needToShowBackBtn) setShowBackBtn(true);
		if (goToSearch && location.pathname !== SEARCH_PATH) {
			historyPush(SEARCH_PATH);
		}
	};

	const handleOnFocus = () => {
		if (DEVICE_TYPE.type === 'ios') window.dispatchEvent(new Event('keyboardDidShow'));
	};

	const handleOnBlur = () => {
		if (DEVICE_TYPE.type === 'ios') window.dispatchEvent(new Event('keyboardDidHide'));
	};

	return (
		<div
			className={classNames(
				{ 'search-bar': searchBar, 'search-module': !searchBar },
				className,
			)}
		>
			<div className={classNames({ container: searchBar, 'with-upgrade': withUpgradeBtn })}>
				<div className="search-field">
					<input
						ref={inputField}
						className={classNames(
							'notranslate',
							{
								'search-bar': searchBar,
								'active-back': !hideBackBtn && (!!searchValue || showBackBtn),
								'active-clear': !!searchValue,
							},
							color,
						)}
						type="text"
						placeholder={placeholder || t('search.search')}
						value={searchValue}
						onChange={handleOnChange}
						onClick={onClick}
						onFocus={handleOnFocus}
						onBlur={handleOnBlur}
						data-name="search_input"
					/>
					<span className="icon icon-search" />
					<Button
						type="icon-link"
						className="back-btn"
						iconName="icon-arrow-back"
						onClick={goToPreviousRoute}
						btnType="button"
					/>
				</div>
			</div>
		</div>
	);
};

export default memo(SearchInput);
